/* iter55g17 — Shared save-state tracker for the editor.
 *
 * Both `useChapterMutations` (manual edits) and `useEditorAiActions`
 * (AI Accept-suggestion) write to `chaptersApi.update`. Rather than
 * threading two parallel state machines through EditorPanel, we hand
 * each hook a `reportSave(fn)` callback that wraps their write in a
 * uniform status transition:  idle → saving → (saved | error) → idle.
 *
 * The `saved` state auto-resets to `idle` after `HOLD_MS` so the pill
 * doesn't stick forever.
 *
 * Return shape:
 *   { state, lastSavedAt, lastError, track }
 *
 *   • `state`       — 'idle' | 'saving' | 'saved' | 'error'
 *   • `lastSavedAt` — ISO string of the most recent successful write
 *   • `lastError`   — user-facing string, cleared on next success
 *   • `track(promise, label?)` — wraps a save promise; returns the
 *     underlying promise so callers can chain.
 */
import { useCallback, useRef, useState } from 'react';

const HOLD_MS = 1800;

export function useSaveState() {
  const [state,      setState]      = useState('idle');
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [lastError,   setLastError]   = useState(null);
  const holdTimerRef = useRef(null);

  const track = useCallback(async (promise, _label) => {
    if (holdTimerRef.current) {
      window.clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
    setState('saving');
    setLastError(null);
    try {
      const value = await promise;
      setState('saved');
      setLastSavedAt(new Date().toISOString());
      holdTimerRef.current = window.setTimeout(() => {
        setState('idle');
        holdTimerRef.current = null;
      }, HOLD_MS);
      return value;
    } catch (err) {
      setState('error');
      setLastError(
        err?.response?.data?.detail || err?.message || 'Could not save.',
      );
      throw err;
    }
  }, []);

  return { state, lastSavedAt, lastError, track };
}
